import React from 'react';
import { Box, Typography, Button, Alert, Container } from '@mui/material';
import { ErrorOutlined, Refresh } from '@mui/icons-material';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; reset: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console in development
    if (import.meta.env.MODE === 'development') {
      console.error('Error boundary caught error:', error, errorInfo);
    }
    
    // Here you could also log to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Custom fallback component
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} reset={this.handleReset} />;
      }

      // Default error UI
      return (
        <Container maxWidth="md">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              textAlign: "center",
              gap: 3
            }}>
            <ErrorOutlined sx={{ fontSize: 64, color: 'error.main' }} />
            
            <Typography variant="h4" component="h1" gutterBottom>
              Något gick fel
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: "600px"
              }}>
              Ett oväntat fel har inträffat. Vi beklagar besväret. Försök att ladda om sidan
              eller kontakta support om problemet kvarstår.
            </Typography>

            {import.meta.env.MODE === 'development' && this.state.error && (
              <Alert severity="error" sx={{ mt: 2, textAlign: 'left', maxWidth: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Error Details (Development Only)
                </Typography>
                <Typography variant="body2" component="pre" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  fontSize: '0.75rem',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  {this.state.error.message}
                  {this.state.error.stack && '\n\nStack trace:\n' + this.state.error.stack}
                </Typography>
              </Alert>
            )}

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center"
              }}>
              <Button
                variant="contained"
                onClick={this.handleReset}
                startIcon={<Refresh />}
              >
                Försök igen
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Ladda om sidan
              </Button>
              
              <Button
                variant="text"
                onClick={() => window.location.href = '/'}
              >
                Gå till startsidan
              </Button>
            </Box>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType<{ error: Error; reset: () => void }>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...(fallback && { fallback })}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default ErrorBoundary;